import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION = "eventsSuggestions";

// Thrown by claim() when the suggestion's pickedUpBy no longer matches what the
// caller rendered. Carries user-ready copy.
export class ClaimConflictError extends Error {}

const toRows = (snapshot) => snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

class EventSuggestionAPI {
  static collectionName = COLLECTION;

  /** Live list of every suggestion, newest first. Returns the unsubscribe. */
  static subscribeAll(onData, onError) {
    const q = query(collection(db, COLLECTION), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => onData(toRows(snapshot)), onError);
  }

  /** Live list of the suggestions a host has claimed, in any status. */
  static subscribeClaimedBy(vendorID, onData, onError) {
    const q = query(collection(db, COLLECTION), where("pickedUpBy", "==", vendorID));
    return onSnapshot(q, (snapshot) => onData(toRows(snapshot)), onError);
  }

  static async setStatus(suggestionId, status) {
    await updateDoc(doc(db, COLLECTION, suggestionId), { status });
  }

  /** Unclaim: back to pending with nobody holding it. */
  static async returnToPool(suggestionId) {
    await updateDoc(doc(db, COLLECTION, suggestionId), {
      pickedUpBy: null,
      pickedUpAt: null,
      status: "pending",
    });
  }

  static async deleteSuggestion(suggestionId) {
    await deleteDoc(doc(db, COLLECTION, suggestionId));
  }

  /**
   * Claim/assign a suggestion only if its pickedUpBy still matches what the
   * operator was looking at (`expected`). A blind updateDoc let two hosts claim
   * the same idea in a race, and two claimed copies become duplicate paid
   * events. Throws ClaimConflictError when the precondition fails.
   */
  static async claim(suggestionId, expected, targetVendorID) {
    await runTransaction(db, async (transaction) => {
      const ref = doc(db, COLLECTION, suggestionId);
      const snap = await transaction.get(ref);
      if (!snap.exists()) {
        throw new ClaimConflictError("This idea no longer exists. It may have been deleted.");
      }
      const currentOwner = snap.data().pickedUpBy || null;
      // Already owned by the target: nothing to change, treat as success.
      if (currentOwner === targetVendorID) return;
      if (currentOwner !== expected) {
        throw new ClaimConflictError("Someone else just claimed this idea. Refresh to see who has it.");
      }
      transaction.update(ref, {
        pickedUpBy: targetVendorID,
        pickedUpAt: new Date().toISOString(),
      });
    });
  }

  /**
   * Mark a suggestion as turned into a real event. Call ONLY after the event
   * write has succeeded: opening the create form is not proof an event exists,
   * so an abandoned conversion has to stay pending and retryable.
   *
   * The eventsSuggestions rules let a claim holder change only pickedUpBy,
   * pickedUpAt, status and convertedAt, so nothing else can be added here (a
   * convertedEventId would fail for hosts).
   */
  static async markConverted(suggestionId) {
    await updateDoc(doc(db, COLLECTION, suggestionId), {
      status: "converted",
      convertedAt: serverTimestamp(),
    });
  }

  /**
   * The clientConfig/features `suggestionVideosEnabled` flag, which gates
   * GENERATION of suggestion clips only. Default on: only an explicit false
   * turns it off.
   */
  static async isVideoGenerationEnabled() {
    const snap = await getDoc(doc(db, "clientConfig", "features"));
    return !(snap.exists() && snap.data()?.suggestionVideosEnabled === false);
  }
}

export default EventSuggestionAPI;
